import { useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";
import { useEffect, useState } from "react";
import { SignalRGroup } from "src/components";
import { useSignalR } from "src/hooks";
import { queryKeys, TaskItemDto, useGetTeamTaskItems, useGetTeamTaskItemSubtasks } from "src/http";

const useGetTeamTaskItemsTree = (teamSlug: string) => {
  const { data, isLoading } = useGetTeamTaskItems(teamSlug);
  const { data: subtaskData, fetchNextPage } = useGetTeamTaskItemSubtasks(teamSlug);
  const [taskItems, setTaskItems] = useState<TaskItemDto[] | undefined>(undefined);
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  useEffect(() => {
    if (teamSlug !== undefined) {
      signalR.joinGroup(SignalRGroup.TeamTaskItems());
      return () => signalR.leaveGroup(SignalRGroup.TeamTaskItems());
    }
  }, [signalR, teamSlug]);

  useEffect(() => {
    const handleShouldRefetchTeamTaskItems = () => {
      queryClient.invalidateQueries(queryKeys.taskItems.allTeamTaskItems);
      queryClient.invalidateQueries(queryKeys.taskItems.allTeamTaskItemSubtasks);
    };

    signalR.onShouldRefetchTeamTaskItems(handleShouldRefetchTeamTaskItems);
    return () => signalR.offShouldRefetchTeamTaskItems(handleShouldRefetchTeamTaskItems);
  }, [queryClient, signalR]);

  useEffect(() => {
    if (data !== undefined) {
      setTaskItems(data.taskItems);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  async function fetchTaskItemSubtasks(taskItemGuid: string) {
    if (taskItems === undefined) {
      return;
    }

    let subtasks: TaskItemDto[] = [];

    // If subtasks are already cached, get them from the cache.
    if (subtaskData?.pageParams.some((pageParam) => pageParam === taskItemGuid)) {
      subtasks = getTaskItemSubtasksFromCache(taskItemGuid);
    } else {
      let fetchResult = await fetchNextPage({ pageParam: taskItemGuid });

      if (fetchResult.data?.pageParams.some((pageParam) => pageParam === taskItemGuid)) {
        let pageIndex = fetchResult.data.pageParams.indexOf(taskItemGuid);
        subtasks = fetchResult.data.pages[pageIndex].taskItems;
      }
    }

    const updatedTaskItems = produce(taskItems, (draft) => {
      let matchingTaskItems = searchTaskItems(draft, (taskItem) => taskItem.guid === taskItemGuid);
      matchingTaskItems?.forEach((taskItem) => {
        taskItem.subtasks = subtasks;
      });
    });

    setTaskItems(updatedTaskItems);
  }

  function getTaskItemSubtasksFromCache(taskItemGuid: string) {
    if (subtaskData === undefined) {
      return [];
    }

    let pageIndex = subtaskData.pageParams.indexOf(taskItemGuid);
    if (pageIndex === -1) {
      return [];
    }

    let subtasks = subtaskData.pages[pageIndex].taskItems;

    // Check to see if the subtask has any of its subtasks already loaded into the cache.
    let subtasksWithSubtasks = produce(subtasks, (draft) => {
      draft.forEach((subtask) => {
        subtask.subtasks = getTaskItemSubtasksFromCache(subtask.guid);
      });
    });

    return subtasksWithSubtasks;
  }

  return {
    data: taskItems,
    isLoading,
    fetchTaskItemSubtasks,
  };
};

const searchTaskItems = (
  taskItems: TaskItemDto[],
  predicate: (taskItem: TaskItemDto) => boolean
): TaskItemDto[] | undefined => {
  var results: TaskItemDto[] = [];

  for (var i = 0; i < taskItems.length; i++) {
    var taskItem = taskItems[i];

    if (predicate(taskItem)) {
      results.push(taskItem);
    }

    if (taskItem.subtasks.length > 0) {
      var subtaskResults = searchTaskItems(taskItem.subtasks, predicate);

      if (subtaskResults) {
        results.push(...subtaskResults);
      }
    }
  }

  if (results.length > 0) {
    return results;
  } else {
    return undefined;
  }
};

export { useGetTeamTaskItemsTree };
